import React, { useCallback, useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import TransactionDetails from './TransactionDetails';
import TransactionLogs from './TransactionLogs';
import TransactionProvider from '../../components/TransactionProvider';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import { providers as providersApi } from '@afosto/order/api/Providers';
import { transactions as transactionsApi } from '@afosto/order/api/Transactions';
import translations from './translations';
import RefundDialog from '../../components/RefundDialog';
import { payments as paymentsApi } from '@afosto/order/api/Payments';
import { useSnackbar } from 'notistack';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';

const TransactionView = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:transactions:create']);
  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const { secret } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [isFetchingTransaction, setIsFetchingTransaction] = useState(true);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  /**
   * Fetch a transaction for the provided secret.
   * @returns {Promise}
   */
  const fetchTransaction = useCallback(
    async isMounted => {
      setIsFetchingTransaction(true);

      try {
        const response = await transactionsApi.getTransaction(secret);
        const { data } = response;

        const paymentProvidersResponse = await providersApi.listProviders().catch(() => ({}));
        const { data: paymentProvidersData = [] } = paymentProvidersResponse ?? {};

        const formattedProviders = paymentProvidersData.reduce((acc, provider) => {
          const methods = (provider?.methods ?? []).reduce((methodAcc, method) => {
            const issuers = (method?.issuers ?? []).reduce(
              (issuerAcc, issuer) => ({ ...issuerAcc, [issuer.code]: issuer }),
              {},
            );

            return { ...methodAcc, [method.code]: { ...(method ?? {}), issuers } };
          }, {});

          return { ...acc, [provider.code]: { ...(provider ?? {}), methods } };
        }, {});

        const formattedPayments = (data?.payments ?? []).reduce(
          (acc, payment) => [
            ...acc,
            {
              ...(payment ?? {}),
              providers: formattedProviders,
            },
          ],
          [],
        );

        if (isMounted) {
          setTransaction({ ...(data ?? {}), payments: formattedPayments });
        }
      } catch (err) {
        if (isMounted) {
          setTransaction(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingTransaction(false);
        }
      }
    },
    [secret],
  );

  const handleRefundPayment = async (paymentId, amountToRefund) => {
    try {
      const confirmRefundResponse = await confirm({
        title: intl.formatMessage(translations.confirmRefund.title),
        description: intl.formatMessage(translations.confirmRefund.description),
        buttons: [
          {
            label: intl.formatMessage(translations.confirmRefund.actions.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmRefund.actions.refund),
            response: 'refund',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
        catchOnCancel: false,
      });

      if (confirmRefundResponse === 'refund') {
        const refundResponse = await paymentsApi.createRefund(transaction?.secret, {
          payment_id: paymentId,
          amount: amountToRefund,
        });

        const { data = {} } = refundResponse;
        const { secret: paymentSecret } = data;

        const confirmExecuteResponse = await confirm({
          title: intl.formatMessage(translations.confirmExecuteRefund.title),
          description: intl.formatMessage(translations.confirmExecuteRefund.description),
          buttons: [
            {
              label: intl.formatMessage(translations.confirmExecuteRefund.actions.park),
              type: 'cancel',
              variant: 'outlined',
              style: {
                marginRight: 'auto',
              },
            },
            {
              label: intl.formatMessage(translations.confirmExecuteRefund.actions.execute),
              response: 'execute',
              type: 'submit',
              variant: 'contained',
              color: 'primary',
            },
          ],
          catchOnCancel: false,
        });

        if (confirmExecuteResponse === 'execute') {
          await paymentsApi.executePaymentTransaction(paymentSecret);
          enqueueSnackbar(intl.formatMessage(translations.feedback.paymentRefunded), {
            variant: 'success',
          });
        }

        fetchTransaction(true);
      }
    } catch (e) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.paymentNotRefunded), {
        variant: 'error',
      });
    } finally {
      setShowRefundDialog(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    fetchTransaction(isMounted);

    return () => {
      isMounted = false;
    };
  }, [fetchTransaction, secret]);

  if (isFetchingTransaction) {
    return <SkeletonPage fullWidth breadcrumbs={2} subNavigation={1} />;
  }

  if (!hasPermissions()) {
    return (
      <Page title={transaction?.number ?? transaction?.id} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <TransactionProvider value={{ transaction, fetchTransaction }}>
      <Page
        title={transaction?.number ?? transaction?.id}
        fullWidth
        subNavigation={{
          current: location.pathname,
          tabs: [
            {
              label: intl.formatMessage(translations.subNavigation.details),
              path: `/transactions/${secret}`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.logs),
              path: `/transactions/${secret}/logs`,
            },
          ],
          onChange: (e, value) => history.replace(value),
        }}
        breadcrumbs={[
          {
            label: intl.formatMessage(translations.breadcrumbs.transactions),
            path: '/transactions',
          },
          {
            label: transaction?.number ?? transaction?.id,
            path: `/transactions/${transaction?.id}`,
            active: true,
          },
        ]}
        linkComponent={Link}
        secondaryActions={[
          ...(transaction?.is_paid
            ? [
                {
                  text: intl.formatMessage(translations.secondaryActions.refund),
                  action: () => setShowRefundDialog(true),
                },
              ]
            : []),
        ]}
      >
        <Switch>
          <Route component={TransactionLogs} path="/transactions/:secret/logs" />
          <Route component={TransactionDetails} path="/transactions/:secret" />
        </Switch>
      </Page>
      <RefundDialog
        open={showRefundDialog}
        onClose={() => setShowRefundDialog(false)}
        onRefund={handleRefundPayment}
        transaction={transaction}
      />
    </TransactionProvider>
  );
};

export default TransactionView;
